import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, ArrowRight, Loader2, Check } from 'lucide-react';
interface CitySelectScreenProps {
  onComplete: () => void;
}
type VerifyState = 'idle' | 'verifying' | 'verified' | 'error';
export function CitySelectScreen({
  onComplete
}: CitySelectScreenProps) {
  const [city, setCity] = useState('');
  const [verifyState, setVerifyState] = useState<VerifyState>('idle');
  // Simulate verification after typing stops
  useEffect(() => {
    if (city.trim().length < 2) {
      setVerifyState('idle');
      return;
    }
    setVerifyState('verifying');
    const timer = setTimeout(() => {
      setVerifyState('verified');
    }, 1200);
    return () => clearTimeout(timer);
  }, [city]);
  const canContinue = verifyState === 'verified';
  return <div className="min-h-screen bg-svt-bg flex flex-col relative overflow-hidden" data-id="element-47">
      {/* Background Effects */}
      <div className="absolute inset-0 bg-grid-pattern opacity-[0.03] pointer-events-none" data-id="element-48"></div>
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[400px] h-[400px] bg-gradient-radial from-svt-purple/15 to-transparent rounded-full blur-[80px] pointer-events-none" data-id="element-49"></div>

      {/* Header */}
      <div className="px-6 pt-6 pb-4 flex items-center gap-3" data-id="element-50">
        <img src="https://cdn.magicpatterns.com/uploads/x3zgz2YSJzc57ZhT1dBqrP/logo.png" alt="SVT Dating" className="h-8 w-auto" data-id="element-51" />
        <span className="font-serif text-xl font-bold tracking-wide text-svt-text" data-id="element-52">
          SVT dating
        </span>
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col justify-center px-6 pb-8 relative z-10" data-id="element-53">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.4
      }} className="flex flex-col items-center mb-8" data-id="element-54">
          <div className="relative mb-6" data-id="element-55">
            <div className="absolute inset-0 bg-gradient-primary opacity-30 blur-2xl rounded-full" data-id="element-56"></div>
            <div className="relative w-16 h-16 rounded-2xl bg-gradient-primary flex items-center justify-center shadow-[0_0_30px_rgba(232,67,147,0.4)]" data-id="element-57">
              <MapPin size={28} className="text-white" data-id="element-58" />
            </div>
          </div>

          <h1 className="text-2xl font-bold text-svt-text mb-2 text-center" data-id="element-59">
            Ваш город
          </h1>
          <p className="text-sm text-svt-text-muted text-center max-w-xs" data-id="element-60">
            Укажите город, в котором SVT-агент будет искать подходящих
            кандидатов
          </p>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.4,
        delay: 0.1
      }} data-id="element-61">
          <label className="block text-xs font-bold tracking-widest uppercase text-svt-text-muted mb-3" data-id="element-62">
            Город
          </label>
          <div className="relative" data-id="element-63">
            <input type="text" value={city} onChange={e => setCity(e.target.value)} placeholder="Например, Москва" className="w-full px-4 py-3 pr-12 rounded-2xl bg-svt-surface border border-svt-border text-svt-text placeholder:text-svt-text-faint focus:outline-none focus:border-svt-pink/50 transition-colors" data-id="element-64" />
            <div className="absolute right-4 top-1/2 -translate-y-1/2" data-id="element-65">
              <AnimatePresence mode="wait" data-id="element-66">
                {verifyState === 'verifying' && <motion.div key="loading" initial={{
                opacity: 0,
                scale: 0.8
              }} animate={{
                opacity: 1,
                scale: 1
              }} exit={{
                opacity: 0,
                scale: 0.8
              }} data-id="element-67">
                    <Loader2 size={18} className="text-svt-pink animate-spin" data-id="element-68" />
                  </motion.div>}
                {verifyState === 'verified' && <motion.div key="verified" initial={{
                opacity: 0,
                scale: 0.5
              }} animate={{
                opacity: 1,
                scale: 1
              }} exit={{
                opacity: 0,
                scale: 0.5
              }} transition={{
                type: 'spring',
                stiffness: 300
              }} className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center" data-id="element-69">
                    <Check size={12} className="text-white" strokeWidth={3} data-id="element-70" />
                  </motion.div>}
              </AnimatePresence>
            </div>
          </div>

          <AnimatePresence data-id="element-71">
            {verifyState === 'verifying' && <motion.p initial={{
            opacity: 0,
            y: -5
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -5
          }} className="text-xs text-svt-text-muted mt-3 pl-2" data-id="element-72">
                Проверяем доступность города...
              </motion.p>}
            {verifyState === 'verified' && <motion.p initial={{
            opacity: 0,
            y: -5
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -5
          }} className="text-xs text-green-400 mt-3 pl-2" data-id="element-73">
                Город найден
              </motion.p>}
          </AnimatePresence>
        </motion.div>
      </div>

      {/* CTA */}
      <div className="px-6 pb-10" data-id="element-74">
        <button onClick={onComplete} disabled={!canContinue} className={`px-12 py-3 rounded-full font-semibold text-base transition-all flex items-center justify-center gap-2 mx-auto ${canContinue ? 'bg-gradient-primary text-white hover:opacity-90 shadow-[0_0_20px_rgba(232,67,147,0.3)]' : 'bg-svt-surface text-svt-text-faint cursor-not-allowed'}`} data-id="element-75">
          Продолжить
          <ArrowRight size={18} data-id="element-76" />
        </button>
      </div>
    </div>;
}